import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminSeatMapService from '../../services/adminSeatMapService';

export default function ManageSeatMapTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName]     = useState('');
  const [deleting, setDeleting]   = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const res = await adminSeatMapService.listTemplates();
      setTemplates(res.data?.templates || []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await adminSeatMapService.createTemplate({ name: newName, width: 1200, height: 800 });
      const tpl = res.data?.map;
      toast.success('Template created');
      setShowModal(false);
      setNewName('');
      navigate(`/admin/seat-map-templates/${tpl.id}/edit`);
    } catch {
      toast.error('Failed to create template');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await adminSeatMapService.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div>
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">Seat Map Templates</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          <i className="fas fa-plus fa-sm mr-1"></i> New Template
        </button>
      </div>

      <p className="text-muted mb-4" style={{ fontSize: 14 }}>
        Create reusable seat map layouts. Organizers can apply these templates when setting up a new event.
      </p>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-5 text-muted">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="fas fa-map fa-3x text-gray-300 mb-3 d-block"></i>
            <p className="text-muted mb-3">No templates yet. Create one to get started.</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              Create First Template
            </button>
          </div>
        </div>
      ) : (
        <div className="row">
          {templates.map(tpl => (
            <div key={tpl.id} className="col-xl-3 col-md-4 col-sm-6 mb-4">
              <div className="card shadow-sm h-100">
                {/* Canvas preview placeholder */}
                <div style={{
                  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf8 100%)',
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                }}>
                  <i className="fas fa-map-marked-alt fa-2x" style={{ color: '#4e73df', opacity: 0.5 }}></i>
                </div>

                <div className="card-body p-3">
                  <h6 className="font-weight-bold mb-1 text-truncate" title={tpl.name}>{tpl.name}</h6>
                  <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                    {tpl.sections_count ?? 0} section{tpl.sections_count !== 1 ? 's' : ''} &middot; {tpl.width} × {tpl.height}
                  </p>
                  <p className="text-muted" style={{ fontSize: 11 }}>
                    Created {new Date(tpl.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div className="card-footer bg-white border-top-0 pt-0 pb-3 px-3 d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-primary flex-fill"
                    onClick={() => navigate(`/admin/seat-map-templates/${tpl.id}/edit`)}
                  >
                    <i className="fas fa-edit fa-sm mr-1"></i> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(tpl.id)}
                    disabled={deleting === tpl.id}
                  >
                    {deleting === tpl.id
                      ? <i className="fas fa-spinner fa-spin fa-sm"></i>
                      : <i className="fas fa-trash fa-sm"></i>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Seat Map Template</h5>
                <button className="close" onClick={() => setShowModal(false)}><span>&times;</span></button>
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  <div className="form-group">
                    <label className="form-label font-weight-bold">Template Name</label>
                    <input
                      className="form-control"
                      placeholder="e.g. Arena Layout, Theatre 500"
                      value={newName}
                      required
                      autoFocus
                      onChange={e => setNewName(e.target.value)}
                    />
                    <small className="text-muted">Organizers will see this name when choosing a template.</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating ? 'Creating…' : 'Create & Open Editor'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
